import React from 'react';
import './Problems.css';

const problems = [
  {
    title: "Your Online Presence Isn't Working",
    desc: "People find you online but don't get in touch, don't buy, or don't come back. You're invisible where it counts.",
    pains: [
      "Enquiries that go nowhere",
      "No way to follow up with website visitors",
      "Competitors showing up above you on Google",
      "No time to post on social media consistently",
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
  },
  {
    title: "Sales Fall Through the Cracks",
    desc: "A lead comes in, you mean to follow up, life gets busy. Three weeks later they've gone to a competitor.",
    pains: [
      "Quotes sent and never chased",
      "No system for following up at the right time",
      "Can't tell which leads are hot vs cold",
      "Losing repeat customers because nobody stayed in touch",
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
    ),
  },
  {
    title: "Operations Eat Your Day",
    desc: "You're so busy running the day-to-day that you never get time to actually work on the business and grow it.",
    pains: [
      "Manually doing things that should be automated",
      "Answering the same customer questions over and over",
      "Scheduling, booking, reminders \u2014 all done by hand",
      "One person doing the job of three",
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
      </svg>
    ),
  },
  {
    title: "Admin is Swallowing You Whole",
    desc: "Invoicing, chasing payments, emails, data entry \u2014 hours every week spent on things that don't grow your business.",
    pains: [
      "Inbox out of control",
      "Chasing overdue payments awkwardly",
      "Paperwork and data entry eating evenings",
      "Nothing gets documented properly",
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
      </svg>
    ),
  },
  {
    title: "You're Flying Blind on Your Numbers",
    desc: "You don't really know your margins, your best customers, or which part of the business makes the most money.",
    pains: [
      "Only look at financials when the accountant asks",
      "No live view of revenue vs target",
      "Can't see which customers or services drive profit",
      "Decisions based on memory, not data",
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
  },
  {
    title: "There Aren't Enough Hours",
    desc: "You're working harder than ever but the business isn't growing proportionally. Something has to give.",
    pains: [
      "Working evenings and weekends just to keep up",
      "No time to think strategically",
      "Can't take holidays without everything stopping",
      "Always reactive, never proactive",
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
  },
];

const Problems: React.FC = () => {
  return (
    <section className="problems" id="problems">
      <div className="problems-inner">
        <div className="problems-head reveal">
          <span className="eyebrow">Sound familiar?</span>
          <h2>If you run a business,<br />you know these <em>exact</em> problems.</h2>
          <p>We've heard every one of these hundreds of times. And we know exactly how to fix them.</p>
        </div>
        <div className="prob-grid reveal">
          {problems.map((p, i) => (
            <div className="prob-card" key={i}>
              <div className="prob-icon">{p.icon}</div>
              <h3>{p.title}</h3>
              <p>{p.desc}</p>
              <ul className="prob-pains">
                {p.pains.map((pain, j) => (
                  <li key={j}>{pain}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Problems;
